import api from "../../../utils/api";
import { unwrap, unwrapList } from "./apiEnvelope";

// ============================================================================
// CRM — HubSpot full-suite integration — frontend service.
//
// The integration is SELF-SERVICE: this tenant pastes its own HubSpot keys in
// the console and the portal is connected — no environment variable, no
// redeploy. Secrets are write-only from here (the API returns a masked preview,
// never the value).
//
// Talks to the torre-de-control-api `crmHubspot` module:
//   • status     (authed): GET    /crm/hubspot/status
//   • keys       (authed): PUT    /crm/hubspot/credentials
//   • forget     (authed): DELETE /crm/hubspot/credentials
//   • test       (authed): POST /crm/hubspot/test
//   • oauth url  (authed): GET  /crm/hubspot/oauth/url
//   • disconnect (authed): POST /crm/hubspot/disconnect
//   • config     (authed): PUT  /crm/hubspot/config
//   • sync       (authed): POST /crm/hubspot/sync?object=&full=
//   • marketing  (authed): POST /crm/hubspot/marketing/sync
//   • push/pull  (authed): POST /crm/hubspot/push | /crm/hubspot/pull
//   • runs       (authed): GET  /crm/hubspot/runs
//   • events     (authed): GET  /crm/hubspot/events
//   • pipelines  (authed): GET  /crm/hubspot/pipelines?object=deals
//   • properties (authed): GET  /crm/hubspot/properties?object=contacts
//
// The continuous sync itself is server-side: Firestore triggers push our edits,
// HubSpot webhooks (POST /crm/hubspot/webhooks) push theirs, and a scheduled job
// reconciles both. This service is the control surface for that machinery.
// ============================================================================

export type SyncDirection = "two_way" | "pull" | "push" | "off";
export type AuthMode = "private_app" | "oauth" | "none";

/** Per-object row of the sync dashboard. */
export interface HubspotObjectStatus {
  key: string;
  label: string;
  hubspotType: string;
  collection: string;
  alsoPush: string[];
  enabled: boolean;
  direction: SyncDirection;
  /** How many of our documents are paired with a HubSpot record. */
  linked: number;
  lastSync: { pulledAt?: number; pushedAt?: number } | null;
}

/** What the server will say about the tenant's keys — presence and a mask only. */
export interface HubspotCredentialsState {
  accessTokenSet: boolean;
  clientIdSet: boolean;
  clientSecretSet: boolean;
  appIdSet: boolean;
  /** `••••abcd`, or null when nothing is stored. */
  accessTokenPreview: string | null;
  clientSecretPreview: string | null;
  /** Public identifiers come back in full so they stay editable. */
  clientId: string | null;
  appId: string | null;
  portalId: string | null;
}

/** Behaviour the tenant tunes from the console. Replaces every env var. */
export interface HubspotSettings {
  syncEnabled: boolean;
  defaultDirection: SyncDirection;
  maxRecordsPerRun: number;
  /** Product stamped on imported records that have none; null = don't stamp. */
  defaultProduct: string | null;
  /** Product per HubSpot deal pipeline (`{ pipelineId: product }`); wins over the default. */
  productByPipeline: Record<string, string>;
  /** Overrides for deployments behind a custom domain; null = derived. */
  publicApiBase: string | null;
  webhookUrl: string | null;
  redirectUri: string | null;
}

/** One permission to tick in HubSpot when creating the credential. */
export interface HubspotScopeGuideItem {
  scope: string;
  /** Granular scopes that replace a legacy one; either set works. */
  alternatives?: string[];
  /** Plain-language reason, shown under the scope. */
  purpose: string;
  /** Only gates one mirror (e.g. marketing emails); the rest syncs without it. */
  optional?: boolean;
}

/**
 * The guide to render. An API that predates `scopeGuide` only sends
 * `requiredScopes`: fall back to it (minus `oauth`, which HubSpot adds by itself
 * and does not exist for keys) so the list is never empty.
 */
export function scopeGuideOf(status: Pick<HubspotStatus, "scopeGuide" | "requiredScopes">): HubspotScopeGuideItem[] {
  if (status.scopeGuide.length > 0) return status.scopeGuide;
  return status.requiredScopes.filter((s) => s !== "oauth").map((scope) => ({ scope, purpose: "" }));
}

/** Plain text for the «Copiar lista» button: one scope per line, alternatives noted. */
export function scopesToCopy(status: Pick<HubspotStatus, "scopeGuide" | "requiredScopes">): string {
  return scopeGuideOf(status)
    .map((item) => {
      const alt = item.alternatives?.length ? `  (o: ${item.alternatives.join(" + ")})` : "";
      return `${item.scope}${alt}${item.optional ? "  [opcional]" : ""}`;
    })
    .join("\n");
}

export interface HubspotStatus {
  connected: boolean;
  authMode: AuthMode;
  oauthConfigured: boolean;
  portalId: string | null;
  hubDomain: string | null;
  scopes: string[];
  connectedAt: string | null;
  webhookPath: string;
  webhookUrl: string | null;
  redirectUri: string;
  publicApiBase: string | null;
  recommendedSubscriptions: string[];
  /**
   * The `webhooks-hsmeta.json` an app on HubSpot's current developer platform
   * needs, already pointed at this deployment. Subscriptions live in a project
   * config file there, not in a settings screen.
   */
  webhooksConfig: {
    uid: string;
    type: string;
    config: {
      settings: { targetUrl: string; maxConcurrentRequests: number };
      subscriptions: {
        crmObjects: { subscriptionType: string; objectType: string; active: boolean }[];
        legacyCrmObjects: unknown[];
      };
    };
  };
  requiredScopes: string[];
  /** What to tick in HubSpot when creating the credential, in display order. */
  scopeGuide: HubspotScopeGuideItem[];
  credentials: HubspotCredentialsState;
  objects: HubspotObjectStatus[];
  dealPipelineId: string;
  ticketPipelineId: string;
  maps: {
    dealStage: Record<string, string>;
    ticketStage: Record<string, string>;
  };
  directions: SyncDirection[];
  newCollections: string[];
  settings: HubspotSettings;
  schedule: string;
  syncEnabled: boolean;
}

export interface HubspotSideStats {
  pulled?: number;
  pushed?: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  truncated?: boolean;
}

export interface HubspotSyncResult {
  object: string;
  label?: string;
  direction?: SyncDirection;
  pull: HubspotSideStats | null;
  push: HubspotSideStats | null;
  error?: string;
}

export interface HubspotRun {
  id: string;
  startedAt: string;
  finishedAt?: string;
  durationMs?: number;
  mode: "full" | "incremental";
  objects: string[];
  results: HubspotSyncResult[];
  owners: { owners: number; linked: number; createdAgents: number } | null;
  errors: string[];
  totals: {
    pulled: number;
    pushed: number;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
  };
}

export interface HubspotEvent {
  id: string;
  eventId: string;
  objectKey: string;
  action: "upsert" | "delete";
  hubspotId: string;
  subscriptionType: string;
  propertyName: string | null;
  changeSource: string | null;
  status: "processed" | "failed";
  outcome: string | null;
  error: string | null;
  occurredAt: string;
}

export interface HubspotPipeline {
  id: string;
  label: string;
  displayOrder: number;
  stages: { id: string; label: string; probability: number | null; closed: boolean }[];
}

export interface HubspotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  groupName: string;
  options: { label: string; value: string }[];
  mapped: boolean;
}

// --- Connection -------------------------------------------------------------

/**
 * Rellena el payload de estado hasta una forma completa.
 *
 * La pantalla desreferencia `settings`, `credentials`, `maps` y `webhooksConfig`
 * en una docena de sitios; si el backend devuelve una forma distinta —una
 * revisión anterior sirviendo durante un rollout, un proxy, un campo renombrado—
 * la página entera se cae con un TypeError en vez de degradar. Normalizar aquí,
 * en el borde, evita repetir guardas por toda la UI y convierte un desajuste de
 * versiones en "algo aparece vacío" en lugar de una pantalla en blanco.
 */
function normalizeStatus(raw: Partial<HubspotStatus> | null | undefined): HubspotStatus {
  const s = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Partial<HubspotStatus>;

  return {
    connected: Boolean(s.connected),
    authMode: s.authMode ?? "none",
    oauthConfigured: Boolean(s.oauthConfigured),
    portalId: s.portalId ?? null,
    hubDomain: s.hubDomain ?? null,
    scopes: Array.isArray(s.scopes) ? s.scopes : [],
    connectedAt: s.connectedAt ?? null,
    webhookPath: s.webhookPath ?? "/crm/hubspot/webhooks",
    webhookUrl: s.webhookUrl ?? null,
    redirectUri: s.redirectUri ?? "",
    publicApiBase: s.publicApiBase ?? null,
    recommendedSubscriptions: Array.isArray(s.recommendedSubscriptions) ? s.recommendedSubscriptions : [],
    webhooksConfig: s.webhooksConfig ?? {
      uid: "webhooks",
      type: "webhooks",
      config: {
        settings: { targetUrl: s.webhookUrl ?? "", maxConcurrentRequests: 10 },
        subscriptions: { crmObjects: [], legacyCrmObjects: [] },
      },
    },
    requiredScopes: Array.isArray(s.requiredScopes) ? s.requiredScopes : [],
    scopeGuide: Array.isArray(s.scopeGuide) ? s.scopeGuide : [],
    credentials: {
      accessTokenSet: false,
      clientIdSet: false,
      clientSecretSet: false,
      appIdSet: false,
      accessTokenPreview: null,
      clientSecretPreview: null,
      clientId: null,
      appId: null,
      portalId: null,
      ...(s.credentials ?? {}),
    },
    objects: Array.isArray(s.objects) ? s.objects : [],
    dealPipelineId: s.dealPipelineId ?? "default",
    ticketPipelineId: s.ticketPipelineId ?? "0",
    maps: {
      dealStage: s.maps?.dealStage ?? {},
      ticketStage: s.maps?.ticketStage ?? {},
    },
    directions: Array.isArray(s.directions) ? s.directions : ["two_way", "pull", "push", "off"],
    newCollections: Array.isArray(s.newCollections) ? s.newCollections : [],
    settings: {
      syncEnabled: true,
      defaultDirection: "two_way",
      maxRecordsPerRun: 2000,
      defaultProduct: null,
      productByPipeline: {},
      publicApiBase: null,
      webhookUrl: null,
      redirectUri: null,
      ...(s.settings ?? {}),
    },
    schedule: s.schedule ?? "",
    // El interruptor vive en `settings`; este campo de nivel superior se
    // conserva por compatibilidad y se deriva de él cuando falta.
    syncEnabled: s.syncEnabled ?? s.settings?.syncEnabled ?? true,
  };
}

export async function fetchHubspotStatus(): Promise<HubspotStatus> {
  const res = await api.get("/crm/hubspot/status");
  return normalizeStatus(unwrap(res));
}

export { normalizeStatus, unwrap };

export async function testHubspotConnection(): Promise<{
  portalId: string | null;
  accountType: string | null;
  timeZone: string | null;
  currency: string | null;
  uiDomain: string | null;
  scopes: string[];
}> {
  const res = await api.post("/crm/hubspot/test");
  return unwrap(res);
}

/** The keys a tenant can paste in. Every one optional — send only what changed. */
export interface HubspotCredentialsInput {
  accessToken?: string;
  clientId?: string;
  clientSecret?: string;
  appId?: string;
  portalId?: string;
}

/**
 * Save this tenant's HubSpot keys. Partial: a field you omit is left untouched,
 * a field sent as "" is cleared. The server answers with whether the new keys
 * actually reached HubSpot, so the UI can confirm without a second round-trip.
 */
export async function saveHubspotCredentials(
  values: HubspotCredentialsInput,
): Promise<{
  verified: boolean;
  verifyError: string | null;
  authMode: AuthMode;
  credentials: HubspotCredentialsState;
}> {
  const res = await api.put("/crm/hubspot/credentials", values);
  return unwrap(res);
}

/** Forget every stored key and the OAuth grant. */
export async function forgetHubspotCredentials(): Promise<{ cleared: boolean; authMode: AuthMode }> {
  const res = await api.delete("/crm/hubspot/credentials");
  return unwrap(res);
}

export async function fetchHubspotOauthUrl(): Promise<string> {
  const res = await api.get("/crm/hubspot/oauth/url");
  return unwrap<{ url?: string }>(res)?.url as string;
}

export async function disconnectHubspot(): Promise<{ cleared: boolean; authMode: AuthMode }> {
  const res = await api.post("/crm/hubspot/disconnect");
  return unwrap(res);
}

// --- Configuration ----------------------------------------------------------

export interface HubspotConfigPatch {
  enabled?: Record<string, boolean>;
  direction?: Record<string, SyncDirection>;
  dealPipelineId?: string;
  ticketPipelineId?: string;
  maps?: {
    dealStage?: Record<string, string>;
    ticketStage?: Record<string, string>;
  };
  settings?: Partial<HubspotSettings>;
}

export async function updateHubspotConfig(patch: HubspotConfigPatch): Promise<void> {
  await api.put("/crm/hubspot/config", patch);
}

// --- Running the sync -------------------------------------------------------

/**
 * Run a sync now.
 * @param objects  limit to these registry keys (empty = everything enabled)
 * @param full     ignore the watermarks and re-read everything (first import)
 */
/**
 * How long the console waits for a sync. The sync runs INSIDE the request and
 * the API allows 300 s; the HTTP client's default is 30 s, so a normal first
 * import (≈1,500 records took 166 s in production) looked like a failure
 * — «timeout of 30000ms exceeded» — while the server finished fine, and pushed
 * people to press the button again and start a second run on top of the first.
 * A little above the server limit, so the server's answer (or its own 504) wins.
 */
export const HUBSPOT_SYNC_TIMEOUT_MS = 310_000;

export async function runHubspotSync(
  objects?: string[],
  full = false,
): Promise<HubspotRun> {
  const res = await api.post(
    "/crm/hubspot/sync",
    {},
    {
      params: { ...(objects?.length ? { object: objects.join(",") } : {}), full: String(full) },
      timeout: HUBSPOT_SYNC_TIMEOUT_MS,
    },
  );
  return unwrap(res) as HubspotRun;
}

/**
 * The status lists «Propietarios (agentes)» as one more row, but it is not a
 * registry object: `/crm/hubspot/sync?object=owners` answers 422. Owners are
 * refreshed inside every sync request, and on demand via `syncHubspotOwners`.
 */
export const HUBSPOT_OWNERS_KEY = "owners";

/**
 * Objects a full import walks, in order. Companies first: a contact links to its
 * company, and copies its name, only if it is already here. Owners are left out
 * (see HUBSPOT_OWNERS_KEY).
 */
export function fullImportOrder(keys: string[]): string[] {
  return keys
    .filter((k) => k !== HUBSPOT_OWNERS_KEY)
    .sort((a, b) => Number(b === "companies") - Number(a === "companies"));
}

export interface HubspotFullImportResult {
  totals: HubspotRun["totals"];
  /** Objects that finished, in the order they ran. */
  done: string[];
  /** The object the import stopped at, if any; the ones after it did not run. */
  failed: { object: string; error: unknown } | null;
}

/**
 * Full import, ONE OBJECT PER REQUEST. The sync runs inside the request and the
 * server allows 300 s; the whole portal in one request sat right at that limit.
 * Stops at the first object that fails — after a client timeout the server is
 * still working on it, and starting the next one would stack two runs.
 */
export async function runHubspotFullImport(
  objects: string[],
  onProgress?: (object: string, index: number, total: number) => void,
): Promise<HubspotFullImportResult> {
  const order = fullImportOrder(objects);
  const result: HubspotFullImportResult = {
    totals: { pulled: 0, pushed: 0, created: 0, updated: 0, skipped: 0, errors: 0 },
    done: [],
    failed: null,
  };
  for (const [i, object] of order.entries()) {
    onProgress?.(object, i, order.length);
    try {
      const run = await runHubspotSync([object], true);
      for (const k of Object.keys(result.totals) as (keyof HubspotRun["totals"])[]) {
        result.totals[k] += Number(run?.totals?.[k]) || 0;
      }
      result.done.push(object);
    } catch (error) {
      result.failed = { object, error };
      break;
    }
  }
  return result;
}

export async function runHubspotMarketingSync(withMembers = false): Promise<unknown> {
  const res = await api.post(
    "/crm/hubspot/marketing/sync",
    {},
    { params: { withMembers: String(withMembers) }, timeout: HUBSPOT_SYNC_TIMEOUT_MS },
  );
  return unwrap(res);
}

export async function syncHubspotOwners(): Promise<{
  owners: number;
  linked: number;
  createdAgents: number;
}> {
  const res = await api.post("/crm/hubspot/sync/owners", {}, { timeout: HUBSPOT_SYNC_TIMEOUT_MS });
  return unwrap(res);
}

export interface HubspotProductsApplied {
  /** Pipelines with a product. */
  pipelines: number;
  /** Imported deals reviewed, and how many changed product. */
  deals: number;
  dealsChanged: number;
  /** Contacts and companies that changed product with their deal. */
  relatedChanged: number;
}

/**
 * Re-apply the pipeline → product mapping to what is already imported. The
 * server only reads and writes the CRM: HubSpot is not called.
 */
export async function applyHubspotProductMapping(): Promise<HubspotProductsApplied> {
  const res = await api.post("/crm/hubspot/products/apply", {}, { timeout: HUBSPOT_SYNC_TIMEOUT_MS });
  const data = (unwrap(res) ?? {}) as Partial<HubspotProductsApplied>;
  return {
    pipelines: Number(data.pipelines) || 0,
    deals: Number(data.deals) || 0,
    dealsChanged: Number(data.dealsChanged) || 0,
    relatedChanged: Number(data.relatedChanged) || 0,
  };
}

/** Send one of OUR documents to HubSpot right now (row-level action). */
export async function pushToHubspot(
  collection: string,
  id: string,
): Promise<{ outcome?: string; skipped?: string }> {
  const res = await api.post("/crm/hubspot/push", { collection, id });
  return unwrap(res);
}

/** Import one HubSpot record by id (testing, or rescuing a missed webhook). */
export async function pullFromHubspot(
  object: string,
  hubspotId: string,
): Promise<{ outcome?: string; skipped?: string }> {
  const res = await api.post("/crm/hubspot/pull", { object, hubspotId });
  return unwrap(res);
}

// --- History & metadata -----------------------------------------------------

export async function fetchHubspotRuns(limit = 20): Promise<HubspotRun[]> {
  const res = await api.get("/crm/hubspot/runs", { params: { limit } });
  return unwrapList(res);
}

export async function fetchHubspotEvents(limit = 50): Promise<HubspotEvent[]> {
  const res = await api.get("/crm/hubspot/events", { params: { limit } });
  return unwrapList(res);
}

export async function fetchHubspotPipelines(object = "deals"): Promise<HubspotPipeline[]> {
  const res = await api.get("/crm/hubspot/pipelines", { params: { object } });
  return unwrapList(res);
}

export async function fetchHubspotProperties(object: string): Promise<HubspotProperty[]> {
  const res = await api.get("/crm/hubspot/properties", { params: { object } });
  return unwrapList(res);
}

/** Deep-link to a record inside HubSpot's own UI. */
export function hubspotRecordUrl(
  portalId: string | null,
  hubspotType: string,
  hubspotId: string,
): string | null {
  if (!portalId || !hubspotId) return null;
  // HubSpot's record URL uses the object's numeric type id for custom objects,
  // but accepts the well-known names for the standard ones.
  return `https://app.hubspot.com/contacts/${portalId}/record/${hubspotType}/${hubspotId}`;
}
